// The player card (click a name in chat or in your party), mute, block, report, and "commend a teammate"
// after a fight. Mutes stay on this device; blocks live on the server and work both ways.
import { COMMEND_TAGS, REPORT_REASONS, reliability, tagForJob, type PlayerCard, type Rep } from '../../../shared/social.js';
import { jobDef } from '../../../shared/jobs.js';
import { factionById } from '../../../shared/factions.js';
import type { ClientMsg } from '../../../shared/protocol.js';

type SocialMsg = Omit<Extract<ClientMsg, { type: 'social' }>, 'type'>;
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; }
function button(text: string, cls: string, fn: () => void): HTMLButtonElement { const b = el('button', cls, text); b.type = 'button'; b.addEventListener('click', e => { e.stopPropagation(); fn(); }); return b; }
const MUTE_KEY = 'tt.muted';

/** "★ 12 · Finished 48 of 50 fights" style summary, or '' for someone new. */
export function repLine(r: Rep | undefined): string {
  if (!r) return '';
  const top = COMMEND_TAGS.filter(t => (r.tags[t.id] ?? 0) > 0).sort((a, b) => (r.tags[b.id] ?? 0) - (r.tags[a.id] ?? 0))[0];
  const parts = [r.commends ? `★ ${r.commends}${top ? ` ${top.icon}` : ''}` : '', r.done + r.left >= 3 ? `${r.done}/${r.done + r.left} fights` : ''].filter(Boolean);
  return parts.join(' · ');
}

export class SocialUI {
  myName = '';
  muted = new Set<string>();
  send: (m: SocialMsg) => void = () => {};
  /** Hooks into the rest of the game for the card's buttons. */
  whisper: (name: string) => void = () => {};
  invite: (name: string) => void = () => {};
  addFriend: (name: string) => void = () => {};
  private pop: HTMLElement | null = null;
  private popFor = '';

  constructor() {
    try { this.muted = new Set(JSON.parse(localStorage.getItem(MUTE_KEY) || '[]')); } catch { /* fine */ }
    addEventListener('pointerdown', e => { if (this.pop && !this.pop.contains(e.target as Node)) this.closePop(); });
    addEventListener('keydown', e => { if (e.key === 'Escape') { this.closePop(); this.closeModal(); } });
  }

  isMuted(name: string): boolean { return this.muted.has(name.toLowerCase()); }
  mute(name: string, on: boolean): void {
    const k = name.toLowerCase(); if (on) this.muted.add(k); else this.muted.delete(k);
    try { localStorage.setItem(MUTE_KEY, JSON.stringify([...this.muted])); } catch { /* not saved */ }
  }

  // ---------- the player card ----------
  /** Open someone's card next to where you clicked. */
  open(name: string, x: number, y: number): void {
    if (!name || name === this.myName) return;
    this.closePop();
    const p = el('div', 'pc-pop'); p.append(el('b', 'pc-name', name), el('div', 'pc-sub', 'Looking…'));
    p.style.left = `${Math.min(innerWidth - 230, Math.max(8, x))}px`; p.style.top = `${Math.min(innerHeight - 260, Math.max(8, y))}px`;
    document.body.append(p); this.pop = p; this.popFor = name.toLowerCase();
    this.send({ op: 'card', name });
    this.actions(p, name, null);
  }

  /** The server's answer: fill in the card. */
  card(c: PlayerCard): void {
    const p = this.pop; if (!p || this.popFor !== c.name.toLowerCase()) return;
    p.replaceChildren();
    const head = el('div', 'pc-head'), j = jobDef(c.job), tag = el('span', 'pc-job', `${j.icon} ${j.name} ${c.level}`); tag.style.background = j.color;
    const pip = el('span', 'pc-pip'); pip.style.background = factionById(c.faction)?.color ?? '#fff';
    head.append(pip, el('b', 'pc-name', c.name), tag);
    p.append(head, el('div', 'pc-sub', c.zone));
    const rel = reliability(c.rep);
    if (c.rep.commends || rel) {
      const rep = el('div', 'pc-rep');
      if (c.rep.commends) rep.append(el('div', '', `★ ${c.rep.commends} commendation${c.rep.commends === 1 ? '' : 's'}`));
      const tags = el('div', 'pc-tags');
      for (const t of COMMEND_TAGS) { const n = c.rep.tags[t.id]; if (n) tags.append(el('span', 'pc-tag', `${t.icon} ${t.label} ${n}`)); }
      if (tags.childElementCount) rep.append(tags);
      if (rel) rep.append(el('div', 'pc-rel', rel));
      p.append(rep);
    } else p.append(el('div', 'pc-new', 'New around here: no commendations yet.'));
    this.actions(p, c.name, c);
  }

  private actions(p: HTMLElement, name: string, c: PlayerCard | null): void {
    p.querySelector('.pc-acts')?.remove();
    const acts = el('div', 'pc-acts'), close = () => this.closePop();
    acts.append(button('Whisper', 'btn', () => { close(); this.whisper(name); }), button('Invite', 'btn', () => { close(); this.invite(name); }));
    if (!c?.friend) acts.append(button('Add friend', 'btn', () => { close(); this.addFriend(name); }));
    const muted = this.isMuted(name);
    acts.append(button(muted ? 'Unmute' : 'Mute', 'btn', () => { this.mute(name, !muted); close(); }));
    acts.append(button(c?.blocked ? 'Unblock' : 'Block', 'btn', () => { close(); this.send({ op: c?.blocked ? 'unblock' : 'block', name }); }));
    acts.append(button('Report…', 'btn danger', () => { close(); this.report(name); }));
    p.append(acts);
  }
  private closePop(): void { this.pop?.remove(); this.pop = null; }

  // ---------- reporting ----------
  private modal: HTMLElement | null = null;
  report(name: string): void {
    this.closeModal();
    const m = el('div', 'modal pc-modal'), card = el('form', 'card');
    card.append(el('h1', '', `Report ${name}`), el('p', 'muted', 'Reports are private. Owners see what you send, plus their recent chat.'));
    const reasons = el('div', 'pc-reasons');
    REPORT_REASONS.forEach((r, i) => { const l = el('label', 'set-check'), inp = el('input'); inp.type = 'radio'; inp.name = 'reason'; inp.value = r.id; inp.checked = i === 0; l.append(inp, document.createTextNode(r.label)); reasons.append(l); });
    const note = el('textarea'); note.maxLength = 500; note.placeholder = 'What happened? (optional)';
    const row = el('div', 'row-btns'), sendB = el('button', 'btn primary', 'Send report'); sendB.type = 'submit';
    row.append(sendB, button('Cancel', 'btn', () => this.closeModal()));
    card.append(reasons, note, row);
    card.addEventListener('submit', e => {
      e.preventDefault();
      const reason = (card.querySelector('input[name=reason]:checked') as HTMLInputElement | null)?.value ?? 'other';
      this.send({ op: 'report', name, reason, note: note.value.trim() }); this.closeModal();
    });
    m.append(card); m.addEventListener('pointerdown', e => { if (e.target === m) this.closeModal(); });
    document.body.append(m); this.modal = m;
  }
  private closeModal(): void { this.modal?.remove(); this.modal = null; }

  // ---------- after a fight with others: commend someone ----------
  private ask: HTMLElement | null = null; private askT = 0;
  commendAsk(battle: string, mates: { char: number; name: string; job: string }[]): void {
    this.ask?.remove(); clearTimeout(this.askT);
    const box = el('div', 'commend');
    box.append(el('b', '', 'Nice fight! Commend a teammate?'), el('small', '', 'One per fight. It shows on their card.'));
    for (const m of mates.slice(0, 5)) {
      const row = el('div', 'cm-row'), j = jobDef(m.job);
      row.append(el('span', 'cm-name', `${j.icon} ${m.name}`));
      const tags = el('div', 'cm-tags'), first = tagForJob(m.job);
      for (const t of [...COMMEND_TAGS].sort((a, b) => (a.id === first ? -1 : b.id === first ? 1 : 0))) {
        tags.append(button(`${t.icon} ${t.label}`, 'cm-tag' + (t.id === first ? ' first' : ''), () => { this.send({ op: 'commend', battle, name: m.name, tag: t.id }); this.closeAsk(); }));
      }
      row.append(tags); box.append(row);
    }
    box.append(button('Not now', 'cm-skip', () => this.closeAsk()));
    document.body.append(box); this.ask = box;
    this.askT = window.setTimeout(() => this.closeAsk(), 60000);
  }
  private closeAsk(): void { this.ask?.remove(); this.ask = null; }
}
